<script>
import { mapGetters, mapActions } from 'vuex'
import SlideUpDown from 'vue-slide-up-down'
import Action from '@/components/Action'
import Icon from '@/components/Icon'
import SelectField from '@/components/SelectField'
import ModalCheckoutOrder from '@/components/ModalCheckoutOrder'
import SkeletonLoader from '@/components/SkeletonLoader'

export default {
  name: 'OrderCart',

  components: {
    Action,
    Icon,
    SelectField,
    ModalCheckoutOrder,
    SlideUpDown,
    SkeletonLoader,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      loadingSearch: false,
      selectedOrderID: null,
      showModalCheckoutOrder: false,
      emptyProduct: {
        id: null,
        name: null,
        products: [],
      },
      isExpanded: true,
      totalPriceLoaded: false,
      windowWidth: window.innerWidth,
    }
  },

  computed: {
    ...mapGetters('orders', [
      'orders',
      'getSelectedOrder',
      'isLoadingOrderCart',
      'hasLoadedOrderCart',
      'getOrderCartError'
    ]),
    ...mapGetters('products', ['getPriceAdd', 'getSelectedProduct']),

    activeOrders: function () {
      return this.orders.filter(function (o) {
        return o.status === 'Em andamento'
      })
    },

    isSmallScreen () {
      return this.windowWidth <= 1280
    },

    orderDetailRoute () {
      if (!this.getSelectedOrder) return null

      return {
        name: 'orders.details',
        params: {
          id: this.getSelectedOrder.id,
        },
      }
    },

    percentualProductPrice () {
      return 1 + (this.getPriceAdd / 100)
    },

    totalProductPrice () {
      return (product) => product.total_price_with_ipi * this.percentualProductPrice
    },

    orderTotal () {
      return this.getSelectedOrder ? this.getSelectedOrder.products.reduce((value, item) => {
        return value + (item.total_price_with_ipi * this.percentualProductPrice)
      }, 0) : 0
    },

    canCheckout () {
      return !!this.orderTotal && this.getSelectedOrder && this.getSelectedOrder.status === 'Em andamento'
    },

    iconName () {
      return this.isExpanded ? 'caret-down' : 'caret-up'
    },

    oldOrderMessage () {
      if (!this.getSelectedOrder || !this.getSelectedOrder.updated) {
        return false // Retorna falso se a data não existir
      }

      const updatedDate = new Date(this.getSelectedOrder.updated) // Converte a string para Date
      const currentDate = new Date() // Data atual
      const diffInDays = (currentDate - updatedDate) / (1000 * 60 * 60 * 24) // Converte a diferença para dias

      return diffInDays > 30 // Retorna true se passou de 30 dias
    },

    hasCartError () {
      return !!this.getOrderCartError
    },

    // O carrinho precisa existir no DOM enquanto carrega ou falha, senão a
    // coluna some e o usuário não sabe se está vazio, carregando ou com erro.
    isCartVisible () {
      return !!this.getSelectedOrder || this.isLoadingOrderCart || this.hasCartError
    },

    shouldShowSelectLoading () {
      if (this.hasCartError) return false

      return this.isLoadingOrderCart ||
        !this.hasLoadedOrderCart ||
        !this.selectedOrderID ||
        this.activeOrders.length === 0
    },

    // "Carrinho vazio" para fins de exibição: sem produtos, seja porque veio
    // vazio do servidor (`[]`), seja porque só o resumo chegou (`undefined`).
    selectedOrderHasNoProducts () {
      const products = this.getSelectedOrder && this.getSelectedOrder.products
      return !Array.isArray(products) || products.length === 0
    },

    // Precisa buscar por id? Só quando os produtos NÃO foram materializados.
    // get_carrinhos monta `products` (mesmo `[]`) apenas no carrinho selecionado;
    // os demais vêm sem a chave. Logo `[]` = carregado e vazio, não refetch;
    // `undefined` = só o resumo veio. Distinguir os dois evita a 2ª chamada
    // duplicada quando o pedido em andamento está vazio.
    selectedOrderProductsNotLoaded () {
      return !Array.isArray(this.getSelectedOrder && this.getSelectedOrder.products)
    },

  },

  watch: {
    $media () {
      this.isExpanded = this.$media.desktop && !this.isSmallScreen
    },

    windowWidth () {
      this.isExpanded = this.$media.desktop && !this.isSmallScreen
    },

    getSelectedOrder: {
      async handler () {
        if (this.getSelectedOrder && this.getSelectedOrder.id !== this.selectedOrderID) {
          if (this.getSelectedOrder.status === 'Finalizado') {
            this.removeSelectOrder()
            this.selectedOrderID = null
            await this.handleSelectedOrder()
          } else {
            this.selectedOrderID = this.getSelectedOrder.id
            // Na hidratação inicial só falta sincronizar o id local: o carrinho
            // selecionado é o mesmo que a carga do Products.vue já trouxe, com
            // produtos. Buscar aqui repetiria a request que acabou de responder
            // — inclusive quando o carrinho veio vazio (`products: []`).
            if (this.selectedOrderProductsNotLoaded) {
              await this.handleSelectedOrder()
            }
          }
        }
        // Atualiza totalPriceLoaded quando getSelectedOrder ficar disponível
        this.updateTotalPriceLoaded()
      },
      immediate: true, // Já executa quando o componente é montado
    },

    getPriceAdd () {
      this.updateTotalPriceLoaded()
    },
  },

  async mounted () {
    this.updateTotalPriceLoaded()

    if (!this.getSelectedOrder) {
      if (this.activeOrders && this.activeOrders.length > 0) {
        this.selectedOrderID = this?.activeOrders[0]?.id
      }
      await this.handleSelectedOrder()
      //
    } else if (this.getSelectedOrder.status !== 'Finalizado') {
      // Se há pedido selecionado e não está finalizado, faz push e carrega produtos se necessário
      this.handlePushOrder()

      // Recarrega só se os produtos ainda não foram materializados. Carrinho
      // vazio (`products: []`) já está carregado e não deve refazer a request.
      if (this.selectedOrderProductsNotLoaded) {
        await this.handleSelectedOrder()
      }
    }

    // Utiliza debounce para otimizar performance
    this.debouncedUpdateWindowWidth = this.debounce(this.updateWindowWidth, 100)
    // Adiciona listener para redimensionamento da janela
    window.addEventListener('resize', this.debouncedUpdateWindowWidth)
  },

  beforeDestroy () {
    // Remover listener quando o componente for destruído
    window.removeEventListener('resize', this.debouncedUpdateWindowWidth)
  },

  methods: {
    ...mapActions(['setHeaderBack', 'setFeedback']),
    ...mapActions('orders', ['selectOrder', 'getOrderCart', 'removeSelectOrder', 'addPushOrder']),
    ...mapActions('products', ['searchProducts', 'getProduct', 'selectProduct']),

    updateTotalPriceLoaded () {
      this.totalPriceLoaded = (this.getPriceAdd != null) && !!this.getSelectedOrder
    },

    async handleSelectedOrder () {
      this.totalPriceLoaded = false

      if (!this.selectedOrderID) {
        try {
          await this.getOrderCart()
        } catch (error) {
          // A falha já está em getOrderCartError e é exibida na coluna.
        } finally {
          this.updateTotalPriceLoaded()
        }
        return
      }
      if (this.getSelectedOrder && this.getSelectedOrder.status === 'Finalizado') { return }

      try {
        await this.getOrderCart({ id: this.selectedOrderID })
      } catch (error) {
        // A falha já está em getOrderCartError e é exibida na coluna.
      } finally {
        this.updateTotalPriceLoaded()
      }
    },

    handleRetryOrderCart () {
      return this.handleSelectedOrder()
    },

    handlePushOrder () {
      const foundOrder = this.activeOrders.find(order => order.id === this.selectedOrderID)

      if (this.getSelectedOrder && (this.selectedOrderID === this.getSelectedOrder.id) && !foundOrder) {
        this.addPushOrder(this.getSelectedOrder)
      }
    },

    handleCheckoutOrder () {
      this.showModalCheckoutOrder = true
    },

    openProduct (search) {
      if (search === this.productCode && (this.getSelectedProduct && this.getSelectedProduct.id === search)) {
        return this.$emit('openSideBarModal')
      }

      if (!this.$media.desktop || this.isSmallScreen) {
        this.toggleCart()
      }

      this.$emit('update:loading', true)
      this.getProduct({ search }).then(() => {
        this.productCode = search
        this.$emit('openSideBarModal')
        return this.$emit('changeTab', 0)
      }).finally(() => {
        this.$emit('update:loading', false)
      })
    },

    toggleCart () {
      this.isExpanded = !this.isExpanded
    },

    updateWindowWidth () {
      this.windowWidth = window.innerWidth
    },

    debounce (func, wait) {
      let timeout
      return function executedFunction (...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    },
  },
}
</script>

<template>
  <div :class="$classes['order-cart-wrapper']" v-if="isCartVisible">
    <div :class="$classes['order-cart']">
      <div :class="$classes['order-cart-header']">
        <h2 v-if="$media.desktop && !isSmallScreen" :class="$classes['order-cart-title']">Pedidos</h2>
        <action v-else :class="$classes['order-cart-header-btn']" @click="toggleCart">
          <h2 :class="$classes['order-cart-title']">Pedidos</h2>
          <icon v-if="!$media.desktop || isSmallScreen" :name="iconName" :class="$classes['order-cart-header-icon']" />
        </action>
      </div>
      <slide-up-down :active="isExpanded" :class="$classes['order-cart-expandable']">
        <div>
          <div v-if="hasCartError" :class="$classes['order-cart-content']">
            <div :class="$classes['order-cart-error']">
              <icon name="triangle-exclamation-solid" :class="$classes['order-cart-error-icon']" />
              <p :class="$classes['order-cart-error-message']">{{ getOrderCartError }}</p>
              <action color="primary" variant="flat" uppercase bold :disabled="isLoadingOrderCart"
                @click="handleRetryOrderCart">
                Tentar novamente
              </action>
            </div>
          </div>
          <div v-else :class="$classes['order-cart-content']">
            <skeleton-loader v-if="shouldShowSelectLoading" type="rect" :width="'302px'" :height="'35px'"
              :rounded="true" />
            <select-field v-else label="Pedido atual" v-model="selectedOrderID" @input="handleSelectedOrder">
              <template v-for="order in activeOrders">
                <option :key="order.id" :value="order.id">{{ order.name }}</option>
              </template>
            </select-field>
            <span v-if="oldOrderMessage" :key="getSelectedOrder.updated" :class="$classes['old-order-cart']">Pedido
              antigo:
              {{ formatDate(getSelectedOrder.updated) }}
            </span>
            <div v-if="shouldShowSelectLoading" :class="$classes['order-cart-products']">
              <skeleton-loader type="rect" :width="'120px'" :height="'20px'" :rounded="true"
                :class="$classes['skeleton-title']" />
              <div :class="$classes['skeleton-table-wrapper']">
                <div :class="$classes['skeleton-table-header']">
                  <skeleton-loader type="rect" :width="'80px'" :height="'16px'" :rounded="true" />
                  <skeleton-loader type="rect" :width="'60px'" :height="'16px'" :rounded="true" />
                  <skeleton-loader type="rect" :width="'70px'" :height="'16px'" :rounded="true" />
                </div>
                <div v-for="i in 3" :key="i" :class="$classes['skeleton-table-row']">
                  <skeleton-loader type="rect" :width="'80px'" :height="'20px'" :rounded="true" />
                  <skeleton-loader type="rect" :width="'60px'" :height="'20px'" :rounded="true" />
                  <skeleton-loader type="rect" :width="'70px'" :height="'20px'" :rounded="true" />
                </div>
                <div :class="$classes['skeleton-table-footer']">
                  <skeleton-loader type="rect" :width="'60px'" :height="'18px'" :rounded="true" />
                  <skeleton-loader type="rect" :width="'70px'" :height="'18px'" :rounded="true" />
                </div>
              </div>
            </div>
            <div v-else :class="$classes['order-cart-products']">
              <h3 :class="$classes['order-cart-products-title']">Produtos</h3>
              <template v-if="selectedOrderHasNoProducts">
                <p :class="$classes['order-cart-products-message']">Nenhum produto adicionado ao pedido.</p>
              </template>
              <table v-else :class="$classes['order-cart-products-table']">
                <div :class="$classes['order-cart-body']">
                  <thead>
                    <tr>
                      <th width="95">Código</th>
                      <th width="100">Qtd.</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(product, index) in getSelectedOrder.products">
                      <tr :key="index">
                        <td>
                          <action
                            v-tooltip="{ content: product.description, delay: { show: 100, hide: 100 }, trigger: 'hover', autoHide: true, hideOnTargetClick: true }"
                            :class="$classes['order-cart-product-btn-info']">
                            <icon :class="$classes['order-cart-product-btn-info-icon']" name="info-circle" />
                          </action>
                          <action :class="$classes['order-cart-products-btn']" @click="openProduct(product.id)">
                            {{ product.id }}
                          </action>
                        </td>
                        <td>{{ product.quantity }}</td>
                        <td>{{ totalProductPrice(product) | currency }}</td>
                      </tr>
                    </template>
                  </tbody>
                </div>
                <tfoot>
                  <tr>
                    <td colspan="2">Total</td>
                    <td><strong>{{ orderTotal | currency }}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div v-if="orderDetailRoute" :class="$classes['order-cart-actions']">
            <action color="primary" variant="flat" :to="orderDetailRoute" uppercase bold>
              <icon name="eye" btnIcon />
              Ver mais
            </action>
            <!--
                                        <action
                                          color="tertiary"
                                          variant="flat"
                                          uppercase
                                          bold
                                          :disabled="!canCheckout"
                                          @click="handleCheckoutOrder"
                                          <icon name="share" btnIcon />
                                          Finalizar
                                        </action>
                                        -->
          </div>
        </div>
      </slide-up-down>
    </div>

    <modal-checkout-order v-if="getSelectedOrder" :active.sync="showModalCheckoutOrder" :order="getSelectedOrder" />
  </div>
</template>

<style module>
@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.old-order-cart {
  letter-spacing: 0.1em;
  font-size: 12px;
  color: #D14040;
  font-weight: bold;
  text-align: center;
  position: relative;
  animation: slideDown 1s ease-in-out forwards;
}

.order-cart-wrapper {
  padding: 10px 0;
  position: relative;
}

.order-cart-wrapper:before {
  content: '';
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  right: 0;
  height: 10px;
  background: url('~@/assets/images/ellipse.png') repeat-x top left;
}

.order-cart-wrapper:after {
  content: '';
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 0;
  right: 0;
  height: 10px;
  background: url('~@/assets/images/ellipse.png') repeat-x bottom left;
}

.order-cart {
  background: #fff;
  box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.25);
  padding: 10px 20px;
}

.order-cart-header {
  display: flex;
  align-items: center;
}

.order-cart-title {
  flex: 1;
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
}

.order-cart-header-btn {
  display: flex;
  width: 100%;
  justify-content: flex-start;
  text-align: left;
}

.order-cart-header-icon {
  color: #a1a1a1;
}

.order-cart-expandable {
  overflow: hidden;
}

.order-cart-content {
  margin-top: 20px;
  margin-bottom: 25px;
  min-height: 200px;
}

.order-cart-products {
  margin-top: 20px;
  display: grid;
}

.order-cart-products-table {
  width: 100%;
}

.order-cart-products-table th {
  font-size: 12px;
  color: #a1a1a1;
  text-transform: uppercase;
  text-align: left;
  height: 25px;
  vertical-align: top;
}

.order-cart-products-table td {
  font-size: 14px;
  color: #292929;
  text-align: left;
  height: 25px;
}

.order-cart-products-table tfoot td {
  text-transform: uppercase;
  height: 25px;
  vertical-align: bottom;
}

.order-cart-products-table tfoot tr {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 45px;
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
  column-gap: 88px;
  justify-content: space-between;
}

.order-cart-body {
  overflow: auto;
  width: auto;
  max-height: 40vh;
}

.order-cart-body::-webkit-scrollbar {
  width: 4px;
}

.order-cart-body::-webkit-scrollbar-track {
  background-color: transparent;
}

.order-cart-body::-webkit-scrollbar-thumb {
  background-color: #a2a2a2;
  border-radius: 5px;
}

.order-cart-body::-webkit-scrollbar-thumb:hover {
  background-color: #888;
}

.order-cart-products-title {
  text-transform: uppercase;
  font-size: 14px;
  margin-bottom: 15px;
}

.order-cart-products-message {
  font-size: 12px;
  color: #A1A1A1;
}

.order-cart-product-btn-info {
  display: inline-block;
  color: #4A94D4;
  margin-right: 5px;
}

.order-cart-product-btn-info-icon {
  width: 16px;
  height: 16px;
}

.order-cart-products-btn {
  display: inline-block;
  font-size: 14px;
  color: #4A94D4;
  text-decoration: underline;
  line-height: 20px;
}

.order-cart-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.order-cart-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  min-height: 200px;
}

.order-cart-error-icon {
  width: 32px;
  height: 32px;
  color: #D14040;
}

.order-cart-error-message {
  font-size: 13px;
  line-height: 18px;
  color: #292929;
}

.skeleton-title {
  margin-bottom: 15px;
}

.skeleton-table-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 302px;
  gap: 12px;
}

.skeleton-table-header {
  display: grid;
  grid-template-columns: 77px 85px 95px;
  gap: 20px;
  padding-bottom: 8px;
}

.skeleton-table-row {
  display: grid;
  grid-template-columns: 77px 85px 95px;
  gap: 20px;
  align-items: center;
}

.skeleton-table-footer {
  display: grid;
  grid-template-columns: 50px 86px 95px;
  gap: 20px;
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
}

.skeleton-table-footer :first-child {
  grid-column: 1 / 3;
}

.skeleton-table-footer :last-child {
  justify-self: end;
}

@media (max-height: 899px) {
  .order-cart-body {
    overflow: auto;
    width: auto;
    max-height: 30vh;
  }
}

@media (max-width: 767px) {
  .order-cart-products-table tfoot tr {
    display: inline-grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 45px;
    margin-top: 15px;
    column-gap: 115px;
  }
}
</style>
